import { ParseError, type ParseResult, type ParsedCategory, type ParsedItem } from './types';

const BOM = '﻿';

function stripBom(s: string): string {
  return s.startsWith(BOM) ? s.slice(1) : s;
}

function splitLines(text: string): string[] {
  return stripBom(text).split(/\r?\n/);
}

function parseDanishNumber(raw: string): { value: number | null; ok: boolean } {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, ok: true };
  // Danish: "1.234,56" -> "1234.56"
  const normalized = trimmed.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (Number.isNaN(n)) return { value: null, ok: false };
  return { value: Math.round(n), ok: true };
}

export function parseFasteBudgetCsv(text: string): ParseResult {
  const lines = splitLines(text);
  const rows = lines.map((line) => line.split(';'));

  // Row 2 (index 1) — year in cell 0
  const yearRow = rows[1];
  const yearRaw = yearRow?.[0]?.trim();
  if (!yearRaw || !/^\d{4}$/.test(yearRaw)) {
    throw new ParseError('missing or invalid year row (row 2, cell 0)');
  }
  const csvYear = Number(yearRaw);
  if (csvYear < 2000 || csvYear > 2100) {
    throw new ParseError(`year out of range: ${csvYear}`);
  }

  // Header row — must contain "Udgifter" in cell 0
  const headerIdx = rows.findIndex((r) => r[0]?.trim() === 'Udgifter');
  if (headerIdx === -1) {
    throw new ParseError('missing header row (starting with "Udgifter")');
  }

  const warnings: string[] = [];
  const categories: ParsedCategory[] = [];
  let pending: ParsedItem[] = [];

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i]!;
    // Skip fully-empty rows
    if (row.every((c) => c.trim() === '')) continue;

    const nameCell = row[0]?.trim() ?? '';
    if (nameCell === '') continue;

    // "I alt" alone (grand total) — skip
    if (nameCell === 'I alt') continue;

    // "I alt <SECTION>" — section terminator
    if (nameCell.startsWith('I alt ')) {
      const catName = nameCell.slice('I alt '.length).trim();
      if (catName && pending.length > 0) {
        categories.push({ name: catName, items: pending });
      }
      pending = [];
      continue;
    }

    // Item row — read 24 cells after the name (12 pairs of budget/actual)
    const cells = row.slice(1);
    if (cells.length < 24) {
      warnings.push(
        `Række ${i + 1} (${nameCell}): færre kolonner end forventet — sidste måneder sat til 0`,
      );
      while (cells.length < 24) cells.push('');
    }

    const budgets: number[] = [];
    const actuals: (number | null)[] = [];
    for (let m = 0; m < 12; m++) {
      const b = parseDanishNumber(cells[m * 2] ?? '');
      const a = parseDanishNumber(cells[m * 2 + 1] ?? '');
      if (!b.ok) {
        warnings.push(
          `Række ${i + 1} (${nameCell}): kunne ikke læse tal '${cells[m * 2]}' — sat til 0`,
        );
        budgets.push(0);
      } else if (b.value === null) {
        warnings.push(
          `Række ${i + 1} (${nameCell}): mangler budget-beløb for måned ${m + 1} — sat til 0`,
        );
        budgets.push(0);
      } else {
        budgets.push(b.value);
      }
      if (!a.ok) {
        warnings.push(
          `Række ${i + 1} (${nameCell}): kunne ikke læse tal '${cells[m * 2 + 1]}' — sat til null`,
        );
        actuals.push(null);
      } else {
        actuals.push(a.value); // may be null
      }
    }

    pending.push({ name: nameCell, budgets, actuals });
  }

  if (categories.length === 0) {
    throw new ParseError('no category terminators (I alt <SECTION>) found');
  }

  const totalItems = categories.reduce((sum, c) => sum + c.items.length, 0);
  if (totalItems === 0) {
    throw new ParseError('no item rows found');
  }

  return { csvYear, categories, warnings };
}
